import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Award, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import type { AuthorDiversityStats } from '@/lib/authorAnalyticsUtils';

interface AuthorInsightsProps {
  diversityStats: AuthorDiversityStats;
}

export function AuthorInsights({ diversityStats }: AuthorInsightsProps) {
  const { uniqueAuthors, totalBooks, diversityScore, topAuthors, readingPattern } = diversityStats;

  const patternDescriptions = {
    diverse: 'You explore a wide variety of authors - always discovering new voices!',
    focused: 'You tend to dive deep with favorite authors - becoming a true expert on their work.',
    balanced: 'You balance discovering new authors with revisiting favorites.',
  };

  const patternColors = {
    diverse: 'text-primary',
    focused: 'text-accent',
    balanced: 'text-secondary',
  };

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Author Diversity Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Diversity Score */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Author Diversity Score</p>
              <p className="text-xs text-muted-foreground mt-1">
                {patternDescriptions[readingPattern]}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-primary">{diversityScore}</div>
              <p className="text-xs text-muted-foreground">out of 100</p>
            </div>
          </div>
          <Progress value={diversityScore} className="h-2" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted/30 rounded-lg text-center">
            <div className="text-2xl font-bold text-primary">{uniqueAuthors}</div>
            <p className="text-xs text-muted-foreground mt-1">Unique Authors</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg text-center">
            <div className="text-2xl font-bold text-secondary">{totalBooks}</div>
            <p className="text-xs text-muted-foreground mt-1">Books Read</p>
          </div>
        </div>

        {/* Reading Pattern Badge */}
        <div className="flex items-center justify-center gap-2">
          <Award className={`w-4 h-4 ${patternColors[readingPattern]}`} />
          <Badge variant="outline" className="text-sm">
            Reading Pattern: <span className={`ml-1 font-bold ${patternColors[readingPattern]}`}>
              {readingPattern.charAt(0).toUpperCase() + readingPattern.slice(1)}
            </span>
          </Badge>
        </div>

        {/* Top Authors */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-accent" />
            <p className="text-sm font-medium">Most Read Authors</p>
          </div>
          <div className="space-y-2">
            {topAuthors.slice(0, 5).map((author, index) => (
              <div key={author.author} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-xs font-bold text-accent flex-shrink-0">
                    {index + 1}
                  </div>
                  <span className="font-medium truncate">{author.author}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="secondary" className="text-xs">
                    {author.count} book{author.count !== 1 ? 's' : ''}
                  </Badge>
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {author.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
